import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { IMeetingTypesService } from "services/MeetingTypesService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { MeetingTypeModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  MeetingTypes: new Array<MeetingTypeModel>(),
  LoadingMeetingTypes: false,
  EditingMeetingTypes: false,
  SavedColumnOrder: new Array<string>(),
};

export const meetingTypesSlice = createSlice({
  name: "meetingTypes",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getMeetingTypes.pending, (state) => {
        state.LoadingMeetingTypes = true;
      })
      .addCase(getMeetingTypes.fulfilled, (state, action) => {
        state.MeetingTypes = action.payload;
        state.LoadingMeetingTypes = false;
      })
      .addCase(getMeetingTypes.rejected, (state) => {
        state.LoadingMeetingTypes = false;
      })
      .addCase(createMeetingType.pending, (state) => {
        state.EditingMeetingTypes = true;
      })
      .addCase(updateMeetingType.pending, (state) => {
        state.EditingMeetingTypes = true;
      })
      .addCase(deleteMeetingType.pending, (state) => {
        state.EditingMeetingTypes = true;
      })
      .addCase(createMeetingType.rejected, (state) => {
        state.EditingMeetingTypes = false;
      })
      .addCase(updateMeetingType.rejected, (state) => {
        state.EditingMeetingTypes = false;
      })
      .addCase(deleteMeetingType.rejected, (state) => {
        state.EditingMeetingTypes = false;
      })
      .addCase(createMeetingType.fulfilled, (state) => {
        toast.success("Meeting Type Created");
        state.EditingMeetingTypes = false;
      })
      .addCase(updateMeetingType.fulfilled, (state) => {
        toast.success("Meeting Type Updated");
        state.EditingMeetingTypes = false;
      })
      .addCase(deleteMeetingType.fulfilled, (state) => {
        toast.success("Meeting Type Deleted");
        state.EditingMeetingTypes = false;
      })
      .addCase(getMeetingTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateMeetingTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.meta.arg;
        toast.success("Column order saved");
      });
  },
});

export const getMeetingTypes = createAsyncThunk("MeetingTypes/GetMeetingTypes", async () => {
  const meetingTypesService = container.get<IMeetingTypesService>(SERVICE_TYPES.MeetingTypesService);
  return await meetingTypesService.getMeetingTypes();
});

export const createMeetingType = createAsyncThunk<void, { name: string }, { state: RootState }>(
  "meetingTypes/CreateMeetingTypes",
  async ({ name }, thunkAPI) => {
    const meetingTypesService = container.get<IMeetingTypesService>(SERVICE_TYPES.MeetingTypesService);
    await meetingTypesService.createMeetingType(name);
    thunkAPI.dispatch(getMeetingTypes());
  }
);

export const updateMeetingType = createAsyncThunk<void, { meetingTypeModel: MeetingTypeModel }, { state: RootState }>(
  "meetingTypes/UpdateMeetingType",
  async ({ meetingTypeModel }, thunkAPI) => {
    const meetingTypesService = container.get<IMeetingTypesService>(SERVICE_TYPES.MeetingTypesService);
    await meetingTypesService.updateMeetingType(meetingTypeModel);
    thunkAPI.dispatch(getMeetingTypes());
  }
);

export const deleteMeetingType = createAsyncThunk<void, { meetingTypeID: number }, { state: RootState }>(
  "meetingTypes/DeleteMeetingType",
  async ({ meetingTypeID }, thunkAPI) => {
    const meetingTypesService = container.get<IMeetingTypesService>(SERVICE_TYPES.MeetingTypesService);
    await meetingTypesService.deleteMeetingType(meetingTypeID);
    thunkAPI.dispatch(getMeetingTypes());
  }
);

export const getMeetingTypesTableColumnOrder = createAsyncThunk(
  "meetingTypes/GetMeetingTypesTableColumnOrder",
  async () => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    return await userSettingsService.getColumnOrder(SETTING_TYPES.MeetingTypesTableColumnKey);
  }
);

export const updateMeetingTypesTableColumnOrder = createAsyncThunk(
  "meetingTypes/UpdateMeetingTypesTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.MeetingTypesTableColumnKey);
  }
);

export const selectMeetingTypes = (state: RootState) => {
  return state.meetingTypes.MeetingTypes;
};
export const selectLoadingMeetingTypes = (state: RootState) => {
  return state.meetingTypes.LoadingMeetingTypes;
};
export const selectEditingMeetingTypes = (state: RootState) => {
  return state.meetingTypes.EditingMeetingTypes;
};

export const selectSavedColumnOrder = (state: RootState) => {
  return state.meetingTypes.SavedColumnOrder;
};

export default meetingTypesSlice.reducer;
